import { Check, CircleAlert, Info, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { shortText } from '../lib/format.ts';
import { motionPresets } from '../lib/motionPresets.ts';
import { useToasts } from '../lib/toast.ts';
import type { ToastItem, ToastTone } from '../lib/toast.ts';
import { Button } from './Button.tsx';
import { IconButton } from './IconButton.tsx';
import { IconTile } from './IconTile.tsx';
import type { IconTileTone } from './IconTile.tsx';
import { Spinner } from './Spinner.tsx';

const TONE: Record<ToastTone, { icon: ReactNode; tile: IconTileTone }> = {
  neutral: { icon: <Info />, tile: 'neutral' },
  success: { icon: <Check />, tile: 'success' },
  error: { icon: <CircleAlert />, tile: 'danger' },
  working: { icon: <Spinner size={14} />, tile: 'working' },
};

/**
 * One toast (components.md, "Feedback"): a small status tile, a short title in at most two
 * lines with any long path or word cut from the middle (the full text in its tooltip), a dim
 * caption under it, one quiet action, and a close that shows on hover (always there for the
 * keyboard and on touch). The raised surface with the menu shadow; it rises 4 px and fades in
 * (200 ms), fades out (120 ms), and is still under reduced motion (the app's MotionConfig).
 * Paused while hovered or focused.
 */
function ToastCard({ t }: { t: ToastItem }) {
  const dismiss = useToasts((s) => s.dismiss);
  const [paused, setPaused] = useState(false);
  const remaining = useRef(t.duration);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    remaining.current = t.duration;
  }, [t.id, t.createdAt, t.duration]);

  useEffect(() => {
    if (!t.duration || paused) return;
    startedAt.current = Date.now();
    const timer = setTimeout(() => dismiss(t.id), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current -= Date.now() - startedAt.current;
    };
  }, [t.id, t.duration, t.createdAt, paused, dismiss]);

  const title = shortText(t.title);
  const tone = TONE[t.tone];
  return (
    <motion.li
      layout
      {...motionPresets.arrive}
      transition={motionPresets.move}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      data-toast={t.tone}
      className="group/toast pointer-events-auto flex w-(--toast-width) max-w-full items-start gap-3 rounded-card border border-border bg-raised p-3 shadow-elevation-2"
    >
      <IconTile icon={tone.icon} tone={tone.tile} size="sm" />
      {/* The role sits inside the list item: an li that is a status is no longer a list item (axe 'list'). */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5" role={t.tone === 'error' ? 'alert' : 'status'}>
        <p className="line-clamp-2 text-ui font-medium text-fg wrap-anywhere" title={title !== t.title ? t.title : undefined}>
          {title}
        </p>
        {t.description && <p className="line-clamp-2 text-caption text-fg-subtle wrap-anywhere">{shortText(t.description)}</p>}
      </div>
      {t.action && (
        <Button
          size="sm"
          variant="ghost"
          className="shrink-0 text-accent-text hover:text-accent-text"
          onClick={() => {
            t.action?.onClick();
            dismiss(t.id);
          }}
        >
          {t.action.label}
        </Button>
      )}
      <IconButton
        label="Dismiss"
        icon={<X />}
        size="xs"
        className="shrink-0 opacity-0 transition-opacity duration-(--duration-fast) group-focus-within/toast:opacity-100 group-hover/toast:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
        onClick={() => dismiss(t.id)}
      />
    </motion.li>
  );
}

/** Renders toasts bottom right, newest at the bottom, 8 px apart. Mounted once by the app shell; call `toast()` from anywhere. */
export function Toaster() {
  const toasts = useToasts((s) => s.toasts);
  return (
    <ol aria-live="polite" aria-label="Notifications" data-toast-region="" className="toast-region pointer-events-none fixed bottom-4 right-4 z-(--z-toast) flex flex-col items-end gap-2 max-sm:left-4">
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastCard key={t.id} t={t} />
        ))}
      </AnimatePresence>
    </ol>
  );
}
